import { useEffect, useState } from "react";

/** The manifest link notify/index.html (the HTML every /notify* request gets)
 * has in its head, and web-demo's index.html doesn't. */
export const NOTIFY_MANIFEST = "/notify/manifest.json";

/** sessionStorage: when this tab last reloaded into notify/index.html. */
export const RELOAD_KEY = "notify:document-reload";
/** A second reload this soon means the server did not send notify/index.html
 * (its rewrite isn't there yet): show the page as it is, never loop. */
export const RELOAD_WINDOW_MS = 10_000;

/** Whether this document is web-demo's index.html: its head names another
 * manifest. (No manifest link at all, as in a test DOM, is not.) */
export function onWebDemoDocument(doc: Document = document): boolean {
  const href = doc.querySelector('link[rel="manifest"]')?.getAttribute("href");
  return href != null && href !== NOTIFY_MANIFEST;
}

function reloadedRecently(now: number): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY));
    return last > 0 && now - last < RELOAD_WINDOW_MS;
  } catch {
    return false;
  }
}

function noteReload(now: number) {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(now));
  } catch {
    // Without storage the window can't be kept; reload anyway (one per mount).
  }
}

const reloadPage = () => window.location.replace(window.location.href);

/** Started once per page, so a re-run effect (StrictMode) doesn't undo it. */
let reloadStarted = false;

/**
 * Makes a Notify page run in notify/index.html, the HTML whose head installs
 * a2z Notify (UI-04: iPad Safari's Add to Home Screen takes the manifest of the
 * HTML as served, not a link changed later). A direct open, a push tap or a
 * reload already get it from the server. An in-app navigation from a web-demo
 * page (Google sign-in's /oauth/callback, then /notify/) is still in index.html,
 * so this reloads the same URL once. Returns true while that reload is under
 * way (render nothing of the page). The other way, a Notify document that
 * routes to /login keeps the Notify head; no reload.
 */
export function useNotifyDocument(reload: () => void = reloadPage): boolean {
  const [reloading, setReloading] = useState(onWebDemoDocument);
  useEffect(() => {
    if (!reloading || reloadStarted) return;
    const now = Date.now();
    if (reloadedRecently(now)) {
      setReloading(false);
      return;
    }
    reloadStarted = true;
    noteReload(now);
    reload();
  }, [reloading, reload]);
  return reloading;
}

/** Tests only: forget a reload started by an earlier test. */
export function resetNotifyDocumentForTest() {
  reloadStarted = false;
}
